import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import ContentFooter from "../../../ui/layout/page/content/footer";
import NavigationBar from "../../../ui/layout/page/content/footer/navigation-bar";
import FormContainer from "../../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import TypographyItemContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import Email from "../../shared/components/email";
import FirstName from "../../shared/components/first-name";
import PasswordField from "../../shared/components/formik/text-input/password";
import Gender from "../../shared/components/gender";
import LastName from "../../shared/components/last-name";
import Phone from "../../shared/components/phone";
import { RegistrationModel } from "../types";
import useAccountFormValidationSchema from "./validator";
import TermsOfServiceCheckbox from "../../../ui/widgets/checkbox/checkbox-terms-of-service";
import { OnTermsAndConditionsRetrievedApi } from "iam-client/dist/wrapper/shared";
import { LanguageContext } from "../../../context/languageContext";
import { Term } from "iam-client/dist/client/shared/termsOfServices";
import Info from "../../../ui/widgets/info";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { CancelApplicationContext } from "../../../context/cancelApplicationContext";

interface AccountFormProps {
  isUpdateMode?: boolean;
  terms: Term[];
  initial: RegistrationModel;
  submitHandler: (model: RegistrationModel) => void;
  previous: () => void;
  iamHandler?: OnTermsAndConditionsRetrievedApi;
}

const dataPath = "admin.";

const AccountForm = ({
  isUpdateMode = false,
  terms,
  initial,
  submitHandler,
  previous,
  iamHandler,
}: AccountFormProps): React.JSX.Element => {
  const { language } = useContext(LanguageContext);
  const { setCancelDialogActive } = useContext(CancelApplicationContext);
  const { getMessage } = useMessageSource();

  return (
    <Formik
      key={language}
      initialValues={initial}
      validationSchema={useAccountFormValidationSchema(isUpdateMode)}
      onSubmit={(values, formikActions) => {
        if (iamHandler) {
          submitHandler(values);
          iamHandler.proceed(true);
        } else {
          // when we are updating the user, no iam handle is available.
          submitHandler(values);
        }
        formikActions.setSubmitting(false);
      }}
    >
      {({ values, handleSubmit, submitCount, isSubmitting, errors }) => {
        return (
          <Form>
            <FormContainer>
              <Gender forceReRender dataPath={dataPath} />
              <InputContainer>
                <FirstName
                  forceReRender
                  dataPath={dataPath}
                  value={values.admin.firstName}
                />
              </InputContainer>
              <InputContainer>
                <LastName
                  forceReRender
                  dataPath={dataPath}
                  value={values.admin.lastName}
                />
              </InputContainer>
              <InputContainer>
                <Phone
                  dataPath={dataPath}
                  value={values.admin.mobile}
                  isMobile
                />
              </InputContainer>
              <InputContainer>
                <Email
                  forceReRender
                  dataPath={dataPath}
                  value={values.admin.emailBusiness}
                  isPrivate={false}
                  helperUnderInput={
                    !isUpdateMode &&
                    getMessage("Person.emailBusiness-helperUnderInput")
                  }
                />
              </InputContainer>
              {!isUpdateMode && (
                <InputContainer>
                  <PasswordField
                    forceReRender
                    name="password"
                    label={getMessage("Account.password")}
                    helperUnderInput={getMessage("Account.password-info")}
                  />
                </InputContainer>
              )}
              <TypographyItemContainer>
                <Info>
                  {getMessage("CommunicationChannelVerification-info")}
                </Info>
              </TypographyItemContainer>
              {!isUpdateMode && <TermsOfServiceCheckbox terms={terms} />}
            </FormContainer>
            <ContentFooter>
              <InvalidFormError
                isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
              />
              <NavigationBar
                onCancel={() => setCancelDialogActive(true)}
                cancelButtonText={getMessage("Onboarding.cancel")}
                isSubmitting={isSubmitting}
                backButtonText={getMessage("Onboarding.back")}
                nextButtonText={getMessage("Onboarding.continue")}
                onBack={previous}
                onNext={() => {
                  handleSubmit();
                }}
              />
            </ContentFooter>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AccountForm;
